import {
  Badge,
  Box,
  Button,
  Flex,
  FormLabel,
  Heading,
  Input,
  InputAddon,
  InputGroup,
  Select,
  Spacer,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import Paginator from '../../../components/paginator'
import { formatDateTime } from '../../../helpers'
import { EntregaDeTrabalhoService } from '../../../services/EntregaDeTrabalho/EntregaDeTrabalhoService'
import { EntregaDeTrabalhoDeleteDialog } from './EntregaDeTrabalhoDeleteDialog'
import { EntregaDeTrabalhoReviewModal } from './EntregaDeTrabalhoReviewModal'
import { EntregaDeTrabalhoViewModal } from './EntregaDeTrabalhoViewModal'
import { courseFormat, secondsToTime, showAlert, ucFirst } from './helper'
import {
  EEntregaDeTrabalhoType,
  IEntregaDeTrabalho,
  IListFilters,
  IReviewTimeStatistics,
  IStatistics,
  statusColors
} from './models'

export default function EntregaDeTrabalhosPage() {
  const service = new EntregaDeTrabalhoService()
  const cancelRef = useRef()
  const [statistics, setStatistics] = useState<IStatistics>()
  const [reviewStatistics, setReviewStatistics] = useState<IReviewTimeStatistics>()
  const [entregaDeTrabalhos, setEntregaDeTrabalhos] = useState<IEntregaDeTrabalho[]>([])
  const [length, setLength] = useState(25)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [draw, setDraw] = useState(0)
  const [modalId, setModalId] = useState<number>()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false)
  const { isOpen: deleteDialogIsOpen, onOpen: deleteDialogOnOpen, onClose: deleteDialogIsClose } = useDisclosure()
  const [deleteId, SetDeleteId] = useState<number>()
  const [teachers, setTeachers] = useState<string[]>([])
  const [courses, setCourses] = useState<{ format: string; name: string }[]>([])
  const [filters, setFilters] = useState<IListFilters>({
    status: '',
    search: '',
    teacher: '',
    type: '',
    course: {
      format: '',
      name: ''
    },
    created_at: {
      from: '',
      to: ''
    }
  })

  const loadEntregasDeTrabalho = async () => {
    const leadsList = await service.list({
      draw,
      length,
      start: (page - 1) * length,
      order: [{ column: 0, dir: 'desc' }],
      filters
    })

    setDraw(draw + 1)

    setTotalPages(Math.ceil(leadsList.recordsFiltered / length))

    setEntregaDeTrabalhos(leadsList.data)
    loadStatistics()
  }

  const loadStatistics = async () => {
    setStatistics(await service.loadStatistics(filters))
    setReviewStatistics(await service.loadReviewTimeStatistics(filters))
  }

  useEffect(() => {
    loadEntregasDeTrabalho()
  }, [page, length, filters])

  useEffect(() => {
    loadTeachers()
    loadCourses()
  }, [])

  const handleOpenReviewModal = async (id: number) => {
    setModalId(id)
    setIsReviewModalOpen(true)
  }

  const handleOpenModal = async (id: number) => {
    setModalId(id)
    setIsModalOpen(true)
  }

  const handleDeleteDialog = (id: number) => {
    deleteDialogOnOpen()
    SetDeleteId(id)
  }

  const loadTeachers = async () => {
    fetch('https://abra.com.br/get-professores.php').then((response) => {
      response.json().then(({ data }) => {
        setTeachers(data.map(({ name }: { name: string }) => name))
      })
    })
  }

  const loadCourses = async () => {
    const [presenciais, onlineAoVivo] = await Promise.all([
      fetch('https://abra.com.br/get-cursos.php?tipo-curso=cursos-presenciais')
        .then((response) => response.json())
        .then(({ cursos }) => cursos.map((course: string) => ({ format: 'presencial', name: course }))),
      fetch('https://abra.com.br/get-cursos.php?tipo-curso=cursos-ead-ao-vivo')
        .then((response) => response.json())
        .then(({ cursos }) => cursos.map((course: string) => ({ format: 'online-ao-vivo', name: course })))
    ])

    setCourses([...presenciais, ...onlineAoVivo])
  }

  return (
    <>
      <div className="row mb-3">
        <div className="col-12 col-md-6 d-flex align-items-center">
          <Heading as="h1" size="lg" marginBottom="5" mb="10" textTransform="uppercase">
            Entregas de Trabalho
          </Heading>
        </div>
        <div className="col-12 col-md-2 d-flex align-items-center justify-content-end">
          <Box textAlign="right">
            <Text
              fontSize="md"
              color="gray.500"
              textAlign="right"
              fontWeight="bold"
              mr="2"
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
            >
              Contagem
            </Text>
            <Text
              fontSize="md"
              color="gray.500"
              textAlign="right"
              mr="2"
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
            >
              Total de entregas: {statistics?.total ?? '-'}
            </Text>
            <Text
              fontSize="md"
              color="gray.500"
              textAlign="right"
              mr="2"
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
            >
              Pendentes: {statistics?.pendente ?? '-'}
            </Text>
            <Text
              fontSize="md"
              color="gray.500"
              textAlign="right"
              mr="2"
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
            >
              Aprovados: {statistics?.aprovado ?? '-'}
            </Text>
            <Text
              fontSize="md"
              color="gray.500"
              textAlign="right"
              mr="2"
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
            >
              Reprovados: {statistics?.reprovado ?? '-'}
            </Text>
          </Box>
        </div>
        <div className="col-12 col-md-4 d-flex align-items-center">
          <Table size="sm">
            <Tbody>
              <Tr>
                <Th></Th>
                <Th>Sem Atraso</Th>
                <Th>Com Atraso</Th>
              </Tr>
              <Tr>
                <Th>Revisados</Th>
                <Td>{reviewStatistics?.reviewed.not_delayed}</Td>
                <Td>{reviewStatistics?.reviewed.delayed}</Td>
              </Tr>
              <Tr>
                <Th>Pendentes</Th>
                <Td>{reviewStatistics?.not_reviewed.not_delayed}</Td>
                <Td>{reviewStatistics?.not_reviewed.delayed}</Td>
              </Tr>
              <Tr>
                <Th>Tempo médio</Th>
                <Td colSpan={2}>{secondsToTime(parseInt(reviewStatistics?.average_time_in_seconds || '0'))}</Td>
              </Tr>
            </Tbody>
          </Table>
        </div>
      </div>
      <div className="row mb-3">
        <div className="col">
          <FormLabel htmlFor="teacher" fontSize="sm" fontWeight="normal">
            Status
          </FormLabel>
          <Select
            id="status"
            size="sm"
            value={filters.status}
            onChange={(e) =>
              setFilters({ ...filters, status: e.target.value as '' | 'PENDENTE' | 'APROVADO' | 'REPROVADO' })
            }
          >
            <option value="">Selecione</option>
            <option value="PENDENTE">Pendente</option>
            <option value="APROVADO">Aprovado</option>
            <option value="REPROVADO">Reprovado</option>
          </Select>
        </div>
        <div className="col">
          <FormLabel htmlFor="teacher" fontSize="sm" fontWeight="normal">
            Professor
          </FormLabel>
          <Select
            id="teacher"
            size="sm"
            value={filters.teacher}
            onChange={(e) => setFilters({ ...filters, teacher: e.target.value })}
          >
            <option value="">Selecione</option>
            {teachers.map((teacher) => (
              <option key={teacher} value={teacher}>
                {teacher}
              </option>
            ))}
          </Select>
        </div>
        <div className="col">
          <FormLabel htmlFor="course" fontSize="sm" fontWeight="normal">
            Curso
          </FormLabel>
          <Select
            id="course"
            size="sm"
            value={JSON.stringify(filters.course)}
            onChange={(e) => {
              const course = JSON.parse(e.target.value)
              setFilters({ ...filters, course })
            }}
          >
            <option value={JSON.stringify({ format: '', name: '' })}>Selecione</option>
            {courses
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((course) => (
                <option key={`${course.format}-${course.name}`} value={JSON.stringify(course)}>
                  {course.name} - {courseFormat[course.format as 'presencial' | 'online' | 'online-ao-vivo']}
                </option>
              ))}
          </Select>
        </div>
        <div className="col">
          <FormLabel htmlFor="type" fontSize="sm" fontWeight="normal">
            Tipo
          </FormLabel>
          <Select
            id="type"
            size="sm"
            value={filters.type}
            onChange={(e) =>
              setFilters({ ...filters, type: e.target.value as '' | 'trabalho-de-conclusão' | 'pesquisa-de-campo' })
            }
          >
            <option value="">Selecione</option>
            <option value="pesquisa-de-campo">Pesquisa de campo</option>
            <option value="trabalho-de-conclusão">Trabalho de conclusão</option>
          </Select>
        </div>
        <div className="col">
          <FormLabel htmlFor="date-start" fontSize="sm" fontWeight="normal">
            Data de criação
          </FormLabel>
          <InputGroup display="flex" alignItems="center" size="sm">
            <Input
              type="date"
              size="sm"
              variant="outline"
              id="date-start"
              value={filters.created_at.from}
              onChange={(e) => setFilters({ ...filters, created_at: { ...filters.created_at, from: e.target.value } })}
              max={filters.created_at.to}
            />
            <InputAddon>Até</InputAddon>
            <Input
              type="date"
              size="sm"
              variant="outline"
              id="date-end"
              value={filters.created_at.to}
              onChange={(e) => setFilters({ ...filters, created_at: { ...filters.created_at, to: e.target.value } })}
              min={filters.created_at.from}
            />
          </InputGroup>
        </div>
      </div>
      <Flex mb="4">
        <Flex flex="1" alignItems="center">
          <FormLabel htmlFor="per-page" fontSize="sm" fontWeight="normal">
            Mostrar
          </FormLabel>
          <Select
            size="sm"
            variant="outline"
            id="per-page"
            w="75px"
            mr="2"
            onChange={(e) => setLength(parseInt(e.target.value))}
            value={length}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </Select>
          <FormLabel htmlFor="per-page" fontSize="sm" fontWeight="normal">
            resultados por página
          </FormLabel>
        </Flex>
        <Spacer />
        <Flex flex="1" justifyContent="flex-end" alignItems="center">
          <FormLabel htmlFor="search" fontSize="sm" fontWeight="normal" p={0} m={0} mr={2}>
            Pesquisar
          </FormLabel>
          <Input
            type="search"
            size="sm"
            variant="outline"
            id="search"
            w="200px"
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            value={filters.search}
          />
        </Flex>
      </Flex>
      <TableContainer fontSize="smaller" padding="0" mt="5">
        <Table>
          <Thead>
            <Tr>
              <Th p={1}>ID</Th>
              <Th p={1}>Nome</Th>
              <Th p={1}>Curso</Th>
              <Th p={1}>Professor</Th>
              <Th p={1}>Tipo</Th>
              <Th p={1} textAlign="center">
                Nota
              </Th>
              <Th p={1}>Status</Th>
              <Th p={1}>Criado em</Th>
              <Th p={1}>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {entregaDeTrabalhos.length === 0 && (
              <Tr>
                <Td p={3} colSpan={8} textAlign="center">
                  Nenhum resultado encontrado
                </Td>
              </Tr>
            )}
            {entregaDeTrabalhos.map((workSubmission) => (
              <Tr key={workSubmission.id}>
                <Td p={1}>{workSubmission.id}</Td>
                <Td p={1}>{workSubmission.name}</Td>
                <Td p={1}>
                  {workSubmission.course} -{' '}
                  {courseFormat[workSubmission.format as 'presencial' | 'online' | 'online-ao-vivo']}
                </Td>
                <Td p={1}>{workSubmission.teacher}</Td>
                <Td p={1}>{EEntregaDeTrabalhoType[workSubmission.type]}</Td>
                <Td p={1} textAlign="center">
                  {workSubmission.review_grade ? (
                    <Tooltip
                      label={`Validado por ${workSubmission.review_teacher} em ${formatDateTime(
                        workSubmission.review_created_at + 'Z'
                      ).replace(' ', ' às ')}h`}
                    >
                      <Text>{Number(workSubmission.review_grade).toFixed(2)}</Text>
                    </Tooltip>
                  ) : (
                    <Tooltip label="Clique para avaliar" aria-label="Clique para avaliar">
                      <Badge
                        colorScheme="gray"
                        rounded="full"
                        onClick={() => {
                          handleOpenReviewModal(workSubmission.id)
                        }}
                        cursor="pointer"
                      >
                        Avaliar
                      </Badge>
                    </Tooltip>
                  )}
                </Td>
                <Td p={1}>
                  {workSubmission.time_taken_seconds ? (
                    <Tooltip label={`Tempo de revisão: ${secondsToTime(workSubmission.time_taken_seconds)}`}>
                      <Badge colorScheme={statusColors[workSubmission.status]}>{ucFirst(workSubmission.status)}</Badge>
                    </Tooltip>
                  ) : (
                    <Badge colorScheme={statusColors[workSubmission.status]}>{ucFirst(workSubmission.status)}</Badge>
                  )}
                </Td>
                <Td p={1}>
                  <Flex alignItems="center">
                    {new Date(workSubmission.created_at).toLocaleString('pt-BR').replace(',', ' -').slice(0, -3)}{' '}
                    {showAlert(
                      workSubmission.created_at,
                      workSubmission.review_created_at,
                      workSubmission.status === 'PENDENTE'
                    )}
                  </Flex>
                </Td>
                <Td p={1}>
                  <Button
                    onClick={() => handleOpenModal(workSubmission.id)}
                    colorScheme="magenta"
                    variant="outline"
                    size="xs"
                    mb="2"
                    ml="2"
                    mr="0"
                  >
                    <i className="lni lni-eye"></i>
                  </Button>
                  <Link to={`/entrega-de-trabalho/preview/${workSubmission.public_id}`} target="_blank">
                    <Button colorScheme="magenta" variant="outline" size="xs" mb="2" ml="2" mr="0">
                      <i className="lni lni-link"></i>
                    </Button>
                  </Link>
                  <Button
                    onClick={() => handleDeleteDialog(workSubmission.id)}
                    colorScheme="magenta"
                    size="xs"
                    mb="2"
                    ml="2"
                    mr="0"
                  >
                    <i className="lni lni-trash-can"></i>
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setPage(page)
        }}
      />

      <EntregaDeTrabalhoViewModal
        id={modalId}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          loadEntregasDeTrabalho()
        }}
      />
      <EntregaDeTrabalhoReviewModal
        isOpen={isReviewModalOpen}
        onClose={() => {
          setIsReviewModalOpen(false)
          loadEntregasDeTrabalho()
        }}
        id={modalId}
      />
      <EntregaDeTrabalhoDeleteDialog
        id={deleteId}
        isOpen={deleteDialogIsOpen}
        onClose={deleteDialogIsClose}
        leastDestructiveRef={cancelRef}
        onDelete={() => {
          deleteDialogIsClose()
          loadEntregasDeTrabalho()
        }}
      />
    </>
  )
}
